import { map, each } from "lodash";
import { cleanData } from "./dataHelpers";
import { globalParse } from ".././utils/globalHelpers";
import { isDate } from ".././utils/dataHelpers";
import useEquipmentTypes from "../hooks/useEquipmentTypes/useEquipmentTypes";

let knownDate = null;
let knownWeekday = null;

const resetKnownDate = () => {
  knownDate = null;
};

const resetKnownWeekday = () => {
  knownWeekday = null;
};

const formatPhoneNumber = value => {
  if (value) {
    value = value.replace(/\D+/g, "");

    const formatted = value.match(/^(\d{3})(\d{3})(\d{4})$/);
    const formattedWithExt = value.match(/^(\d{3})(\d{3})(\d{4})(\d*)$/);

    if (formatted) {
      return formatted[1] + "-" + formatted[2] + "-" + formatted[3];
    } else if (formattedWithExt) {
      return (
        formattedWithExt[1] +
        "-" +
        formattedWithExt[2] +
        "-" +
        formattedWithExt[3] +
        " x" +
        formattedWithExt[4]
      );
    }
  }
};

const parsePhoneNumber = (value, type) => {
  if (value) {
    value = value.replace(/\D+/g, "");
    const parsedNumber = {
      phone: value.slice(0, 10),
      ext: value.slice(10)
    };
    return parsedNumber[type];
  }
};

const formatTitleCase = text => {
  if (text && text.length) {
    return text.toLowerCase()
      .split(" ")
      .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(" ");
  } else {
    return "";
  }
};

const formatEmpty = (value) => {
  if (!value) {
    return "-";
  } else {
    return value;
  }
};

const formatEmptyNumber = (value, decimals) => {
  if (!value) {
    return "-";
  } else {
    return value.toFixed(decimals);
  }
};

const formatCurrency = (amount, decimal) => {
  if (amount != null) {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    });
    if (decimal === 2) {
      return formatter.format(amount);
    } else {
      return formatter.format(Math.round(amount)).replace(/\D00$/, "");
    }
  }
  else {
    return "-";
  }
};

const formatNumberWithCommas = number => {
  if (number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } else {
    return null;
  }
};

const formatPercent = (value, decimal) => {
  if (value && decimal) {
    return `${(value * 100).toFixed(decimal)}%`;
  } else if (value && !decimal) {
    return `${(value * 100).toFixed(0)}%`;
  } else {
    return "0%";
  }
};

const formatWeight = (amount) => {
  if (amount != null) {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "unit",
      unit: "pound"
    });
    return formatter.format(amount);
  }
  else {
    return "-";
  }
};

const parseAvailability = text => {
  return Promise.resolve()
    .then(function () {
      let object = map(text.split("\n"), function (line) {
        // Remove the cruft
        line = parseHelpers().stripPunctuation(line, " ");
        line = line.trim().replace(globalParse().cruftRegEx, " $ ");

        if (line) return parseBlob(line);
      });
      object = cleanData(object.filter(o => o != undefined));
      return object;
    });
};

const parseBlob = text => {
  function parseLine(string) {
    const { typeModes } = useEquipmentTypes();
    let data = {
      type: "row",
      cities: [],
      states: [],
      mode: "",
      date: null,
      raw: string
    };

    if (!data.date && knownDate) {
      data.date = knownDate;
    }

    let parts = string.toUpperCase().split(/\s+|,|\t|\(|\)|-/);
    parts = parts.filter(p => p != "");
    let city = null;

    function setCity() {
      if (city) {
        // DE by itself is not a valid city, therefore it must be a state
        if (/^de$/i.test(city)) {
          data.states.push(city);
        } else {
          data.cities.push(city);
        }
        city = null;
      }
    }

    each(parts, function (part, index) {
      if (!part || !part.length) {
        return;
      } else {
        let mode = typeModes[part];
        var weekday = globalParse().weekdays[part];
        let dayIndex = parts.indexOf(knownWeekday);
        if (mode != undefined) {
          data.mode = mode;
          parts = parts.splice(index, 1);
          return;
        } else if (isDate(part)) {
          let dateArray = part.split("/");
          let year = dateArray.length === 3 ? dateArray[2].length === 2 ? "20" + dateArray[2] : dateArray[2] : new Date().getFullYear();
          const date = new Date(year, dateArray[0] - 1, dateArray[1]);
          data.date = date;
          knownDate = date;
        } else if (weekday != undefined) {
          let date = new Date();
          if (weekday === 7) {
            if (!knownWeekday || dayIndex < 0 || index < dayIndex) {
              data.date = date;
              knownDate = date;
              knownWeekday = part;
            } else {
              data.date = knownDate;
            }
          } else if (weekday === 8) {
            date = new Date(date.setDate(date.getDate() + 1));
            if (!knownWeekday || dayIndex < 0 || index < dayIndex) {
              data.date = date;
              knownDate = date;
              knownWeekday = part;
            } else {
              data.date = knownDate;
            }
          } else {
            let day = date.getDay();
            while (day != weekday) {
              date = new Date(date.setDate(date.getDate() + 1));
              day = date.getDay();
            }
            if (!knownWeekday || dayIndex < 0 || index < dayIndex) {
              data.date = date;
              knownDate = date;
              knownWeekday = part;
            } else {
              data.date = knownDate;
            }
          }
        } else if (part === "$") {
          setCity();
          return;
        }
      }

      let test = part.toUpperCase().trim();
      part = part.toUpperCase().trim();
      let state = globalParse().states[part];

      if (state) {
        if (/in/i.test(state) && !city) {
          // if no city, ignore to keep it from showing up as Indiana
          // If LA or DE with no city, treat as a city, such as la grange, IL. Otherwise, treat as state
        } else if (/^(la|de)$/i.test(state) && !city) {
          city = part.trim();
        } else {
          if (city) {
            setCity();
          }
          let nextItem = parts[index + 1];
          // If it is 2 letters, we can safely assume it is a state
          if (test.length === 2) {
            data.states.push(state);
          }
          // If the next item is not a part of the city.  (Such as Kansas City)
          else if (parts.length < index + 1 || nextItem === "" || !/^[A-Za-z]+$/.test(nextItem)) {
            data.states.push(state);
          } else if (test.length > 2) {
            city = test;
          }
        }
        // Include . here because it is often used in abbreviations
      } else if (/^[A-Za-z.]+$/.test(part) && part.length > 1 && weekday === undefined) {
        city = ((city ? city : "") + " " + part).trim();
      }
    });
    if (city) {
      setCity();
    }

    return data;
  }
  return parseLine(text);
};

const parseHelpers = t => {
  return {
    stripPunctuation: function (text = t) {
      text = text.replace(/[^\w\s-\x2F,]/g, "");

      return text;
    }
  };
};

const parseStringToNumber = n => {
  return parseFloat(n.toString().replace(/,/g, ""));
};

const removeSpecialCharacters = (text) => {
  return text.replace(/[^A-Z0-9]+/ig, "");
};

const formatPipelineStatus = value => {
  const cleanValue = value ? value.trim() : value;

  switch (cleanValue) {
    case "LEAD":
      return "Lead";
    case "RELATN":
      return "Relationship";
    case "OPEN":
    case null:
      return "Open";
    default:
      return null;
  }
};

export {
  formatPhoneNumber,
  parsePhoneNumber,
  formatTitleCase,
  formatEmpty,
  formatEmptyNumber,
  formatCurrency,
  formatNumberWithCommas,
  formatPercent,
  formatWeight,
  parseAvailability,
  parseBlob,
  parseHelpers,
  parseStringToNumber,
  removeSpecialCharacters,
  formatPipelineStatus,
  resetKnownDate,
  resetKnownWeekday
};